import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, AlertCircle, Mail, RefreshCw, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

export default function AuthPage() {
  const { signIn, signUp, resetPassword, signInWithMicrosoft, user, profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [signupEmail, setSignupEmail] = useState<string>('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState<string>('');
  const [resetSent, setResetSent] = useState(false);

  // Clear corrupted auth state on mount if no user
  useEffect(() => {
    const clearCorruptedAuthState = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // No valid session, clear any corrupted tokens
        try {
          const keys = Object.keys(localStorage);
          keys.forEach((key) => {
            if (key.startsWith("sb-")) {
              localStorage.removeItem(key);
            }
          });
        } catch (error) {
          // Silently fail
        }
      }
    };

    clearCorruptedAuthState();
  }, []);

  // Handle OAuth callback and existing sessions
  useEffect(() => {
    const checkAuth = async () => {
      if (user) {
        // User is authenticated, redirect based on profile status
        if (profile === null) {
          // Profile doesn't exist, go to onboarding
          navigate('/onboarding');
        } else if (profile) {
          // Profile exists, go to dashboard
          navigate('/dashboard');
        }
        // If profile is undefined, still loading, wait
      } else {
        // No user, stay on auth page
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, [user, profile, navigate]);

  // Show loading state while checking authentication
  if (isCheckingAuth && user) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Logging you in...</p>
        </div>
      </div>
    );
  }

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    const { error } = await signIn(email, password);
    
    if (error) {
      const errorMessage = error.message === 'Invalid login credentials' 
        ? 'Invalid email or password. If you just signed up, please check your email to confirm your account first.'
        : error.message;
      setError(errorMessage);
      toast({
        title: "Error signing in",
        description: errorMessage,
        variant: "danger",
      });
    } else {
      toast({
        title: "Welcome back!",
        description: "You have been signed in successfully.",
      });
    }
    
    setIsLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const name = formData.get('name') as string;

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setIsLoading(false);
      return;
    }

    const { error } = await signUp(email, password, name);
    
    if (error) {
      setError(error.message);
      toast({
        title: "Error creating account",
        description: error.message,
        variant: "danger",
      });
    } else {
      setSignupEmail(email);
      setShowConfirmation(true);
    }
    
    setIsLoading(false);
  };

  const handleResendConfirmation = async () => {
    setIsLoading(true);
    setError(null);

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: signupEmail,
    });

    if (error) {
      toast({
        title: "Error resending email",
        description: error.message,
        variant: "danger",
      });
    } else {
      toast({
        title: "Confirmation email resent!",
        description: "Please check your inbox.",
      });
    }

    setIsLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const { error } = await resetPassword(resetEmail);

    if (error) {
      toast({
        title: "Error sending reset email",
        description: error.message,
        variant: "danger",
      });
    } else {
      setResetSent(true);
      toast({
        title: "Reset email sent!",
        description: "Check your inbox for the password reset link.",
      });
    }

    setIsLoading(false);
  };

  const handleMicrosoftSignIn = async () => {
    setIsLoading(true);
    setError(null);

    const { error } = await signInWithMicrosoft();

    if (error) {
      const errorMessage = error.message.includes('redirect') 
        ? 'Unable to redirect to Microsoft. Please check your configuration.'
        : error.message;
      setError(errorMessage);
      toast({
        title: "Error signing in with Microsoft",
        description: errorMessage,
        variant: "danger",
      });
      setIsLoading(false);
    }
    // If successful, the redirect will happen automatically
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block group">
            <div className="flex justify-center mb-4">
              <div className="bg-white/10 p-3 rounded-full group-hover:bg-white/20 transition-colors">
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2 group-hover:text-white/90 transition-colors">
              ComplyManager
            </h1>
          </Link>
          <p className="text-white/80">Access your compliance dashboard</p>
        </div>

        <Card className="bg-white dark:bg-gray-900 shadow-elevated border border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-gray-100">Authentication</CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              Sign in to your account or create a new one
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showConfirmation ? (
              <div className="space-y-4">
                <Alert className="border-primary/20 bg-primary/5">
                  <Mail className="h-4 w-4 text-primary" />
                  <AlertDescription className="text-foreground">
                    <strong className="font-semibold">Check your email!</strong>
                    <p className="mt-2">
                      We've sent a confirmation email to <strong>{signupEmail}</strong>
                    </p>
                    <p className="mt-2 text-sm">
                      Click the link in the email to verify your account and complete the signup process.
                    </p>
                  </AlertDescription>
                </Alert>

                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    onClick={handleResendConfirmation}
                    disabled={isLoading}
                    className="w-full"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Resend confirmation email
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowConfirmation(false);
                      setSignupEmail('');
                    }}
                    className="w-full"
                  >
                    Back to sign in
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {error && (
                  <Alert variant="danger" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Tabs defaultValue="signin" className="space-y-4" onValueChange={() => setError(null)}>
              <TabsList className="grid w-full grid-cols-2 bg-gray-100 dark:bg-gray-800">
                <TabsTrigger value="signin" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100">Sign In</TabsTrigger>
                <TabsTrigger value="signup" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email" className="text-gray-700 dark:text-gray-300">Email</Label>
                    <Input
                      id="signin-email"
                      name="email"
                      type="email"
                      placeholder="Enter your email"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password" className="text-gray-700 dark:text-gray-300">Password</Label>
                    <Input
                      id="signin-password"
                      name="password"
                      type="password"
                      placeholder="Enter your password"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isLoading}
                    variant="hero"
                  >
                    {isLoading ? 'Signing in...' : 'Sign In'}
                  </Button>
                  
                  <Dialog open={showForgotPassword} onOpenChange={(open) => {
                    setShowForgotPassword(open);
                    if (open) {
                      setError(null);
                      setResetSent(false);
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button
                        type="button"
                        variant="link"
                        className="w-full text-sm text-muted-foreground hover:text-primary"
                      >
                        Forgot password?
                      </Button>
                    </DialogTrigger>
                     <DialogContent>
                       <DialogHeader>
                         <DialogTitle>Reset Password</DialogTitle>
                         <DialogDescription>
                           {resetSent ? (
                             "Check your email for the password reset link."
                           ) : (
                             "Enter your email address and we'll send you a link to reset your password."
                           )}
                         </DialogDescription>
                       </DialogHeader>
                       {resetSent ? (
                         <Alert className="border-primary/20 bg-primary/5">
                           <Mail className="h-4 w-4 text-primary" />
                           <AlertDescription className="text-foreground">
                             <p className="font-semibold">Email sent!</p>
                             <p className="mt-1 text-sm">
                               Check your inbox at <strong>{resetEmail}</strong>
                             </p>
                           </AlertDescription>
                         </Alert>
                       ) : (
                         <form onSubmit={handleForgotPassword} className="space-y-4">
                           <div className="space-y-2">
                             <Label htmlFor="reset-email" className="text-gray-700 dark:text-gray-300">Email</Label>
                             <Input
                               id="reset-email"
                               type="email"
                               placeholder="Enter your email"
                               value={resetEmail}
                               onChange={(e) => setResetEmail(e.target.value)}
                               required
                               disabled={isLoading}
                             />
                           </div>
                           <Button type="submit" className="w-full" disabled={isLoading}>
                             {isLoading ? "Sending..." : "Send Reset Link"}
                           </Button>
                         </form>
                       )}
                     </DialogContent>
                  </Dialog>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white dark:bg-gray-900 px-2 text-gray-500 dark:text-gray-400">
                        Or continue with
                      </span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleMicrosoftSignIn}
                    disabled={isLoading}
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23">
                      <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                      <path fill="#f35325" d="M1 1h10v10H1z"/>
                      <path fill="#81bc06" d="M12 1h10v10H12z"/>
                      <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                      <path fill="#ffba08" d="M12 12h10v10H12z"/>
                    </svg>
                    Sign in with Microsoft
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name" className="text-gray-700 dark:text-gray-300">Full Name</Label>
                    <Input
                      id="signup-name"
                      name="name"
                      type="text"
                      placeholder="Enter your full name"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email" className="text-gray-700 dark:text-gray-300">Email</Label>
                    <Input
                      id="signup-email"
                      name="email"
                      type="email"
                      placeholder="Enter your email"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password" className="text-gray-700 dark:text-gray-300">Password</Label>
                    <Input
                      id="signup-password"
                      name="password"
                      type="password"
                      placeholder="Enter your password (min 6 characters)"
                      required
                      disabled={isLoading}
                      minLength={6}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isLoading}
                    variant="hero"
                  >
                    {isLoading ? 'Creating account...' : 'Create Account'}
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white dark:bg-gray-900 px-2 text-gray-500 dark:text-gray-400">
                        Or continue with
                      </span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleMicrosoftSignIn}
                    disabled={isLoading}
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23">
                      <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                      <path fill="#f35325" d="M1 1h10v10H1z"/>
                      <path fill="#81bc06" d="M12 1h10v10H12z"/>
                      <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                      <path fill="#ffba08" d="M12 12h10v10H12z"/>
                    </svg>
                    Sign in with Microsoft
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
              </>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-4">
          <Link 
            to="/" 
            className="text-white/80 hover:text-white text-sm transition-colors inline-flex items-center gap-1"
          >
            <svg 
              className="h-4 w-4" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M10 19l-7-7m0 0l7-7m-7 7h18" 
              />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}